/**
 * @module router/journal
 * GL routes — 3-way app split (ROLE_APP_REORG_PLAN.md Phase 3):
 *   journal    — the GL entry page (cashier scope: record/review journal entries)
 *   gl         — reporting/operational: Ledger, Trial Balance, Financials
 *   gl-config  — config: COA (chart of accounts) + Periods (period close); admin
 * appadmin bypasses all app gating. An admin can hold any combination.
 */
const journalRoutes = [
  {
    path: "/accounting/journal",
    name: "Journal",
    component: () => import("@/views/accounting/JournalView.vue"),
    // Reachable with either "journal" (cashier scope) or "gl" (reporting) — a
    // GL-access admin can record/review entries too. Guard treats app[] as ANY-match.
    // Readers open it too (view-only grants, 2026-11-04): the page hides its edits
    // unless isAdmin — the admin role AND a full grant for this page's app.
    meta: { requiresAuth: true, app: ["journal", "gl"] },
  },
  {
    // Cash Daybook — the GL-side replacement for the Petty Cash daybook, so the
    // report that goes to Bilal outlives that module.
    path: "/accounting/cash-daybook",
    name: "CashDaybook",
    component: () => import("@/views/accounting/CashDaybookView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    // The Petty Cash module was retired 2026-09-23; its daybook, ledger,
    // double-entry and employee-balance URLs land on the Cash Daybook that
    // replaced it, so an old bookmark never meets a blank page.
    path: "/accounting/petty-cash/:view(ledger|journal|employees)?",
    redirect: "/accounting/cash-daybook",
  },
  {
    // Upload scans from the phone; they wait under "To file" until attached (2026-10-28).
    // Uploading GL documents is admin-only in the assets service, so the route is too.
    path: "/accounting/documents/upload",
    name: "UploadDocuments",
    component: () => import("@/views/accounting/UploadDocumentsView.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, app: ["journal", "gl", "warehouse"] },
  },
  {
    path: "/accounting/ledger",
    name: "Ledger",
    component: () => import("@/views/accounting/LedgerView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    path: "/accounting/coa",
    name: "Coa",
    component: () => import("@/views/accounting/CoaView.vue"),
    // Any GL reader may look at the chart; changing it stays with gl-config / accounting
    // admins — the server's requireAppAccess(accounting|gl-config) + requireAdmin.
    meta: { requiresAuth: true, app: ["gl-config", "gl", "accounting"], editApp: ["gl-config", "accounting"] },
  },
  {
    path: "/accounting/trial-balance",
    name: "TrialBalance",
    component: () => import("@/views/accounting/TrialBalanceView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    // Profitability per cost centre (sub-centres rolled up) — Data review
    path: "/accounting/cost-centers",
    name: "CostCenters",
    component: () => import("@/views/accounting/CostCentersView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    // Food cost % by month and place, from the ledger — Data review (was a tab of Financials until 2026-11-01)
    path: "/accounting/food-cost",
    name: "FoodCostRatios",
    component: () => import("@/views/accounting/FoodCostView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    // The VAT return of a quarter from the ledger, for the accountant to file — Data review
    path: "/accounting/vat-return",
    name: "VatReturn",
    component: () => import("@/views/accounting/VatReturnView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    path: "/accounting/financials",
    name: "Financials",
    component: () => import("@/views/accounting/FinancialsView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    path: "/accounting/periods",
    name: "Periods",
    component: () => import("@/views/accounting/PeriodsView.vue"),
    // Any GL reader may see which months are closed; closing stays with gl-config /
    // accounting admins (periods/close: requireAppAccess(accounting|gl-config) + requireAdmin).
    meta: { requiresAuth: true, app: ["gl-config", "gl", "accounting"], editApp: ["gl-config", "accounting"] },
  },
  {
    path: "/accounting/fixed-assets",
    name: "FixedAssets",
    component: () => import("@/views/accounting/FixedAssetsView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    // Costs paid in advance, charged month by month (2026-11-01)
    path: "/accounting/prepaid",
    name: "PrepaidExpenses",
    component: () => import("@/views/accounting/PrepaidExpensesView.vue"),
    meta: { requiresAuth: true, app: "gl" },
  },
  {
    // Staff provisions — gratuity, leave salary, air tickets — from HR (2026-11-01)
    path: "/accounting/staff-provisions",
    name: "StaffProvisions",
    component: () => import("@/views/accounting/StaffProvisionsView.vue"),
    // Per person and worked from salaries: payroll readers only (lib/payroll-access.js).
    meta: { requiresAuth: true, app: ["accounting", "gl"], payroll: true },
  },
];

export default journalRoutes;
